// Feeling taxonomy for the create wizard's Feeling step (Feeling system, 2026-07-09).
//
// Source of truth for the UI: 8 feeling FAMILIES, each with a vibe and its feelings (name + blurb).
// Mirrors FEEL_FAMS in docs/mockups/app-wireframe.html. The wizard submits the array of selected
// feeling NAMES (unchanged API contract); the backend maps those names to art direction + gravity
// (see backend feelingDirection.ts). Names are the contract — keep them in sync with the backend.

export const FEELING_FAMILIES = [
  {
    name: 'Tender',
    vibe: 'warmth, comfort, closeness',
    color: '#D85A30',
    feels: [
      { name: 'Comforting', blurb: 'Soft reassurance — warm muted tones, gentle light, calm space.' },
      { name: 'Loving', blurb: 'Closeness — rosy-gold palette, intimate framing.' },
      { name: 'Grateful', blurb: 'Full-hearted thanks — warm gold, open giving composition.' },
      { name: 'Hopeful', blurb: 'Looking forward — dawn light, fresh pastels, open sky.' },
      { name: 'Peaceful', blurb: 'Serene stillness — cool soft palette, wide calm horizon.' },
    ],
  },
  {
    name: 'Reverent',
    vibe: 'solemn, sacred, dignified',
    color: '#BA7517',
    feels: [
      { name: 'Faithful', blurb: 'Sacred devotion — candlelight, deep jewel tones, luminous.' },
      { name: 'Reverent', blurb: 'Solemn awe — muted shadowed palette, grand and still.' },
      { name: 'Reflective', blurb: 'Contemplation — twilight, desaturated, a lone figure.' },
      { name: 'Honoring', blurb: 'Dignified tribute — rich stately tones, formal composition.' },
    ],
  },
  {
    name: 'Unfiltered',
    vibe: 'honest, bittersweet, real',
    color: '#888780',
    feels: [
      { name: 'Honest and raw', blurb: 'Unvarnished — natural desaturated color, candid, close.' },
      { name: 'Bittersweet', blurb: 'Joy edged with loss — faded warm and cool, light through shadow.' },
      { name: 'Nostalgic', blurb: 'The warmth of memory — sepia, film grain, soft vignette.' },
      { name: 'Wistful', blurb: 'Gentle longing — hazy muted light, distant, quiet.' },
    ],
  },
  {
    name: 'Joyful',
    vibe: 'bright, festive, celebratory',
    color: '#D4537E',
    feels: [
      { name: 'Celebratory', blurb: 'Festive triumph — bright saturated color, dynamic energy.' },
      { name: 'Joyful', blurb: 'Pure delight — vivid warm palette, bright and lively.' },
      { name: 'Festive', blurb: 'Party and occasion — multicolor, sparkle, abundant detail.' },
      { name: 'Radiant', blurb: 'Glowing positivity — luminous gold, sunbeams, bloom.' },
    ],
  },
  {
    name: 'Playful',
    vibe: 'funny, cheeky, light',
    color: '#1D9E75',
    feels: [
      { name: 'Funny', blurb: 'Gag-forward comedy — punchy bright, exaggerated, surprising.' },
      { name: 'Light-hearted', blurb: 'Easy and breezy — cheerful pastels, simple, buoyant.' },
      { name: 'Playful', blurb: 'Whimsical fun — candy palette, bouncy, imaginative.' },
      { name: 'Cheeky', blurb: 'Winking mischief — bold pops of color, sly and tongue-in-cheek.' },
      { name: 'Irreverent', blurb: 'Rule-breaking — clashing color, unexpected juxtaposition.' },
      { name: 'Absurd', blurb: 'Surreal comedy — wild saturation, dreamlike, illogical.' },
    ],
  },
  {
    name: 'Bold',
    vibe: 'epic, heroic, larger than life',
    color: '#378ADD',
    feels: [
      { name: 'Epic', blurb: 'Cinematic grandeur — dramatic contrast, sweeping scale.' },
      { name: 'Heroic', blurb: 'Valor and strength — bronze and steel, rim light, low angle.' },
      { name: 'Triumphant', blurb: 'Victory — gold and banners, raised and radiant.' },
      { name: 'Adventurous', blurb: 'Daring journey — vivid outdoors, motion, open horizon.' },
      { name: 'Fierce', blurb: 'Raw intensity — high-contrast dark, charged and close.' },
      { name: 'Majestic', blurb: 'Awe and splendor — regal jewel tones, vast and ornate.' },
    ],
  },
  {
    name: 'Dreamy',
    vibe: 'romantic, magical, ethereal',
    color: '#7F77DD',
    feels: [
      { name: 'Romantic', blurb: 'Love and tenderness — blush and candlelight, soft focus.' },
      { name: 'Dreamy', blurb: 'Ethereal reverie — pastel haze, floating glow, bokeh.' },
      { name: 'Enchanted', blurb: 'Magical wonder — twilight jewel tones, sparkle, fairytale.' },
    ],
  },
  {
    name: 'Striking',
    vibe: 'moody, edgy, graphic',
    color: '#E24B4A',
    feels: [
      { name: 'Whimsical', blurb: 'Quirky charm — storybook palette, invented detail.' },
      { name: 'Bold and modern', blurb: 'Graphic confidence — high-contrast, few colors, clean.' },
      { name: 'Moody', blurb: 'Introspective dark — desaturated shadow, atmospheric.' },
      { name: 'Dark and gothic', blurb: 'Dramatic gloom — black with crimson or silver, baroque.' },
      { name: 'Rebellious', blurb: 'Defiant edge — grunge and neon on black, raw and kinetic.' },
    ],
  },
];

// Flat list of every feeling name (selection + lookup helpers).
export const ALL_FEELINGS = FEELING_FAMILIES.flatMap((fam) => fam.feels.map((f) => f.name));
